import { useCallback, useMemo, useState } from 'react';

import IconCaretDown from '@/assets/Icons/IconCaretDown.tsx';
import Dropdown from '@/components/atoms/Dropdown.tsx';

export interface PhaseItem {
  id: string;
  name: string;
}

export interface IPhasesSelectorProps {
  phases: PhaseItem[];
  hiddenPhases?: string[];
  onPhasesChange: (hiddenPhases: string[]) => void;
  label?: string;
}

export const TablePhasesSelector = ({
  phases,
  onPhasesChange,
  hiddenPhases,
  label,
}: IPhasesSelectorProps) => {
  const displayLabel = label ?? 'Phases';

  // EWM-1041: `hiddenPhases` makes this a controlled component, and every caller that
  // passes it stores exactly the array `onPhasesChange` hands up and feeds it straight back.
  // Mirroring it into state through an effect therefore re-dispatched a value this component
  // already held — and because the echoed array is a fresh identity, React could not bail
  // out, so each checkbox click bought a second, default-lane commit (the same drip fixed in
  // MetadataField for EWM-1049). The prop now wins during render and the local state is only
  // the uncontrolled fallback, used when no `hiddenPhases` prop is given.
  const [uncontrolledHidePhases, setUncontrolledHidePhases] = useState<string[]>([]);
  const hidePhases = hiddenPhases ?? uncontrolledHidePhases;

  const showHidePhase = useCallback(
    (phaseId: string, visibility: boolean) => {
      const copy = visibility ? hidePhases.filter((d) => d !== phaseId) : [...hidePhases, phaseId];
      // Controlled: the prop wins, so writing the fallback would only schedule an update
      // whose result is never read.
      if (hiddenPhases === undefined) {
        setUncontrolledHidePhases(copy);
      }
      onPhasesChange(copy);
    },
    [hiddenPhases, hidePhases, onPhasesChange],
  );

  const mappedPhases = useMemo(
    () =>
      phases?.map((phase) => {
        return (
          <li
            key={phase.id}
            role='none' // stopPropagation only — the interactive element is the checkbox label inside
            className='flex flex-col'
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className='dark:hover:bg-c4 flex items-center px-4 py-3 hover:bg-(--color-light-grey)'>
              <label className='mb-0 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={!hidePhases.includes(phase.id)}
                  className='form-checkbox mr-1'
                  defaultValue={phase.id}
                  onChange={({ target: { checked } }) => {
                    showHidePhase(phase.id, checked);
                  }}
                />
                <span className='ltr:ml-2 rtl:mr-2 dark:text-white'>{phase.name}</span>
              </label>
            </div>
          </li>
        );
      }),
    [phases, hidePhases, showHidePhase],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DropdownComponent = Dropdown as any;

  if (!phases || phases.length === 0) {
    return null;
  }

  return (
    <DropdownComponent
      placement='bottom-start'
      offset={[0, 10]}
      btnClassName='!flex h-12 items-center gap-1 rounded-md border bg-white px-4 py-3 text-base hover:border-inherit dark:border-white dark:text-white dark:bg-transparent'
      button={
        <div className='flex gap-1'>
          <span className='ltr:mr-1 rtl:ml-1'>{displayLabel}</span>
          <IconCaretDown className='h-5 w-5' />
        </div>
      }
    >
      <ul className='dark:!bg-c16 !my-0 !min-w-[200px] rounded py-3 opacity-100'>{mappedPhases}</ul>
    </DropdownComponent>
  );
};
